import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Fade,
  Button,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Inventory as InventoryIcon,
  LocalOffer as LocalOfferIcon,
  AccountBalanceWallet as WalletIcon,
} from "@mui/icons-material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import * as XLSX from "xlsx";
import {
  useBulkAddItemsToCampaign,
  useCampaignImportCandidates,
} from "../../../../client/hooks/products/promotions.mutations";
import CampaignImportData from "./CampaignImportData";

export default function CampaignGridView({
  campaigns,
  isLoading,
  onViewDetails,
  onEdit,
  onToggleStatus,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const { mutate: exportCandidates } = useCampaignImportCandidates();
  const { mutate: importCandidates } = useBulkAddItemsToCampaign();

  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [isLoadingBtn, setIsLoadingBtn] = useState(false);

  function downloadBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  function handleExportData(campaign) {
    setIsLoadingBtn(true);
    exportCandidates(
      { campaign },
      {
        onSuccess: (blob) => {
          const fileName = `${campaign.name !== undefined ? campaign.name : "Campanha sem nome"}.xls`;
          downloadBlob(blob, fileName);
        },
        onSettled: () => {
          setIsLoadingBtn(false);
        },
      }
    );
  }
  function handleImporData(campaign) {
    setSelectedCampaign(campaign);
    setImportDialogOpen(true);
  }

  const handleFileSelected = async (file) => {
    setIsLoadingBtn(true);
    try {
      const data = await file.arrayBuffer(); // lê como binário
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      // Monta estrutura final
      const payload = {
        promotion_type: selectedCampaign.type,
        items: json.map((row) => ({
          item_id: String(row["Item ID"]).trim(),
          deal_price: Number(
            row["Suggested Discounted Price"] ||
              row["deal_price"] ||
              row["preco"] ||
              row["preço"] ||
              row["price"] ||
              row["Price"] ||
              0
          ),
          offer_id: String(row["Offer Id"]).trim() || null,
        })),
      };
      importCandidates({ promotionId: selectedCampaign.id, data: payload });
      // Aqui você pode fazer o POST com `api.post("/rota", payload)`
    } catch (err) {
      console.error("Erro ao processar o arquivo:", err);
    } finally {
      setIsLoadingBtn(false);
    }
  };

  const handleMenuOpen = (event, campaign) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedCampaign(campaign);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCampaign(null);
  };
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "started":
        return { color: "#00a650", bg: "#e8f5e8" };
      case "pending":
        return { color: "#3483fa", bg: "#e3f2fd" };
      case "finished":
        return { color: "#d32f2f", bg: "#ffebee" };
      default:
        return { color: "#666", bg: "#f5f5f5" };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "short",
      });
    } catch {
      return "N/A";
    }
  };
  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "started":
        return <PlayIcon sx={{ fontSize: 14 }} />;
      case "pending":
        return <PauseIcon sx={{ fontSize: 14 }} />;
      default:
        return null;
    }
  };

  const renderBenefits = (benefits) => {
    if (!benefits) return null;
    
    const getBenefitTypeLabel = (type) => {
      switch (type) {
        case 'REBATE':
          return 'Cashback';
        case 'DISCOUNT':
          return 'Desconto';
        default:
          return type;
      }
    };

    return (
      <Box sx={{ 
        bgcolor: 'rgba(52, 131, 250, 0.04)', 
        p: 1.2, 
        borderRadius: 1.5, 
        border: '1px solid rgba(52, 131, 250, 0.1)',
        mt: 0.5
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.8 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LocalOfferIcon sx={{ color: '#3483fa', fontSize: 12 }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#3483fa', fontSize: '0.7rem' }}>
              Benefits
            </Typography>
          </Box>
          <Chip 
            label={getBenefitTypeLabel(benefits.type)} 
            size="small"
            sx={{ 
              bgcolor: '#e8f5e8', 
              color: '#00a650',
              fontWeight: 600,
              fontSize: '0.6rem',
              height: 16,
              '& .MuiChip-label': {
                px: 0.8
              }
            }}
          />
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
          {benefits.seller_percent && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <WalletIcon sx={{ color: '#00a650', fontSize: 10 }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#00a650', fontSize: '0.65rem' }}>
                Vendedor: {benefits.seller_percent}%
              </Typography>
            </Box>
          )}
          
          {benefits.meli_percent && (
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#ff6600', fontSize: '0.65rem' }}>
              ML: {benefits.meli_percent}%
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        {[...Array(9)].map((_, index) => (
          <Box
            key={index}
            sx={{
              flex: {
                xs: "1 1 100%",
                sm: "1 1 calc(50% - 8px)",
                md: "1 1 calc(33.333% - 12px)",
              },
              minWidth: "280px",
            }}
          >
            <Skeleton
              variant="rectangular"
              height={220}
              sx={{ borderRadius: 2 }}
            />
          </Box>
        ))}
      </Box>
    );
  }

  const filteredCampaigns = campaigns.results || [];

  if (filteredCampaigns.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 8,
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 120,
            height: 120,
            borderRadius: "50%",
            bgcolor: "grey.100",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
          }}
        >
          <TrendingUpIcon sx={{ fontSize: 60, color: "grey.400" }} />
        </Box>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Nenhuma campanha encontrada
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Crie sua primeira campanha para começar a promover seus produtos
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        {filteredCampaigns.map((campaign) => {
          const statusConfig = getStatusColor(campaign.status);

          return (
            <Box
              key={campaign.id}
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(50% - 8px)",
                  md: "1 1 calc(33.333% - 12px)",
                },
                minWidth: "280px",
              }}
            >
              <Fade in>
                <Card
                  sx={{
                    cursor: "pointer",
                    height: "100%",
                    transition: "all 0.3s ease",
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "grey.200",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 32px rgba(52, 131, 250, 0.12)",
                      borderColor: "#3483fa",
                    },
                  }}
                  onClick={() => onViewDetails(campaign)}
                >
                  <CardContent
                    sx={{
                      p: 3,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 2,
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: statusConfig.bg,
                          color: statusConfig.color,
                          fontWeight: 700,
                        }}
                      >
                        {(campaign.name || "C").charAt(0).toUpperCase()}
                      </Avatar>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {" "}
                        <Tooltip title="Exportar campanha">
                          <span>
                            <IconButton
                              size="small"
                              disabled={isLoadingBtn}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExportData(campaign);
                              }}
                              sx={{
                                border: "1px solid #3483fa",
                                bgcolor: "rgba(52, 131, 250, 0.08)",
                                color: "#3483fa",
                                "&:hover": {
                                  bgcolor: "rgba(52, 131, 250, 0.12)",
                                  borderColor: "#2968c8",
                                  transform: "scale(1.05)",
                                },
                                "&:disabled": {
                                  borderColor: "#ccc",
                                  color: "#999",
                                  bgcolor: "rgba(0,0,0,0.02)",
                                },
                                transition: "all 0.2s ease",
                              }}
                            >
                              {isLoadingBtn ? (
                                <CircularProgress size={18} color="inherit" />
                              ) : (
                                <FileDownloadIcon />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Aderir em Massa">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleImporData(campaign);
                            }}
                            sx={{
                              border: "1px solid #00a650",
                              bgcolor: "#00a650",
                              color: "white",
                              "&:hover": {
                                bgcolor: "#008a43",
                                borderColor: "#008a43",
                                transform: "scale(1.05)",
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            <FileUploadIcon />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, campaign)}
                          sx={{
                            bgcolor: "grey.100",
                            "&:hover": { bgcolor: "grey.200" },
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>

                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "grey.800",
                        fontSize: "1rem",
                        mb: 1,
                        lineHeight: 1.3,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {campaign.name || "Campanha sem nome"}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                        mb: 2,
                      }}
                    >
                      ID: {campaign.id || "N/A"}
                    </Typography>

                    <Chip
                      icon={getStatusIcon(campaign.status)}
                      label={campaign.status || "Desconhecido"}
                      size="small"
                      sx={{
                        bgcolor: statusConfig.bg,
                        color: statusConfig.color,
                        fontWeight: 600,
                        fontSize: "0.7rem",
                        height: 24,
                        borderRadius: 2,
                        mb: 1.5,
                        alignSelf: 'flex-start'
                      }}
                    />

                    {/* Campaign Info Section */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        mb: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <PeopleIcon sx={{ color: "#3483fa", fontSize: 16 }} />
                          <Typography variant="caption" color="text.secondary">
                            Tipo
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {campaign.type || "N/A"}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <InventoryIcon
                            sx={{ color: "#ff6600", fontSize: 16 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            Período
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                          {formatDate(campaign.start_date)} - {formatDate(campaign.finish_date)}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Benefits Section */}
                    {renderBenefits(campaign.benefits)}

                    {/* Spacer to push everything up properly */}
                    <Box sx={{ flex: 1 }} />
                  </CardContent>
                </Card>
              </Fade>
            </Box>
          );
        })}{" "}
        <CampaignImportData
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          onFileSelected={handleFileSelected}
          campaign={selectedCampaign}
        />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            minWidth: 160,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            onViewDetails(selectedCampaign);
            handleMenuClose();
          }}
          sx={{ gap: 1 }}
        >
          <VisibilityIcon fontSize="small" />
          Ver detalhes
        </MenuItem>
        <MenuItem
          onClick={() => {
            onEdit(selectedCampaign);
            handleMenuClose();
          }}
          sx={{ gap: 1 }}
        >
          <EditIcon fontSize="small" />
          Editar
        </MenuItem>
        {selectedCampaign?.status?.toLowerCase() === "started" ? (
          <MenuItem
            onClick={() => {
              onToggleStatus(selectedCampaign, "pause");
              handleMenuClose();
            }}
            sx={{ gap: 1 }}
          >
            <PauseIcon fontSize="small" />
            Pausar
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              onToggleStatus(selectedCampaign, "activate");
              handleMenuClose();
            }}
            sx={{ gap: 1 }}
          >
            <PlayIcon fontSize="small" />
            Ativar
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
